/**
 * CrewTab — Friends list management
 *
 * Displays all friends with online status. Online friends sorted to top.
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { FriendCell } from './FriendCell';
import type { FriendCellData } from './FriendCell';

interface CrewTabEvents {
  'player:select': { playerId: string };
  'player:favourite': { playerId: string; isFavourite: boolean };
  'player:gift': { playerId: string };
}

export class CrewTab extends EventEmitter<CrewTabEvents> {
  public element: HTMLElement;
  private listEl: HTMLElement;
  private emptyEl: HTMLElement;
  private cells: Map<string, FriendCell> = new Map();
  private friends: FriendCellData[] = [];

  constructor() {
    super();
    this.element = document.createElement('div');
    this.element.className = 'crew-tab-content';

    this.listEl = document.createElement('div');
    this.listEl.className = 'crew-friend-list';

    this.emptyEl = document.createElement('div');
    this.emptyEl.className = 'crew-empty-state';
    this.emptyEl.innerHTML = `
      <div class="crew-empty-icon">👥</div>
      <div class="crew-empty-title">NO FRIENDS YET</div>
      <div class="crew-empty-text">Search for players to add to your crew</div>
    `;

    this.element.appendChild(this.listEl);
    this.element.appendChild(this.emptyEl);
  }

  setFriends(friends: FriendCellData[]): void {
    this.friends = friends;
    this.renderList();
  }

  updateOnlineStatus(playerId: string, online: boolean): void {
    const friend = this.friends.find(f => f.id === playerId);
    if (friend) {
      friend.isOnline = online;
      const cell = this.cells.get(playerId);
      if (cell) cell.update({ isOnline: online });
      // Re-sort: online friends first
      this.renderList();
    }
  }

  updateFavourite(playerId: string, isFavourite: boolean): void {
    const friend = this.friends.find(f => f.id === playerId);
    if (friend) {
      friend.isFavourite = isFavourite;
      const cell = this.cells.get(playerId);
      if (cell) cell.update({ isFavourite });
    }
  }

  addFriend(friend: FriendCellData): void {
    if (this.friends.some(f => f.id === friend.id)) return;
    this.friends.push(friend);
    this.renderList();
  }

  removeFriend(playerId: string): void {
    this.friends = this.friends.filter(f => f.id !== playerId);
    this.renderList();
  }

  getFriends(): FriendCellData[] {
    return this.friends;
  }

  destroy(): void {
    this.cells.forEach(c => c.destroy());
    this.cells.clear();
    this.element.remove();
    this.removeAllListeners();
  }

  private renderList(): void {
    // Clear existing cells
    this.cells.forEach(c => c.destroy());
    this.cells.clear();
    this.listEl.innerHTML = '';

    // Sort: online first, then alphabetical
    const sorted = [...this.friends].sort((a, b) => {
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      return a.username.localeCompare(b.username);
    });

    // Toggle empty state
    this.emptyEl.style.display = sorted.length === 0 ? 'flex' : 'none';
    this.listEl.style.display = sorted.length === 0 ? 'none' : 'flex';

    for (const friend of sorted) {
      const cell = new FriendCell(friend);
      cell.on('select', (data) => this.emit('player:select', data));
      cell.on('favourite', (data) => this.emit('player:favourite', data));
      cell.on('gift', (data) => this.emit('player:gift', data));
      this.cells.set(friend.id, cell);
      this.listEl.appendChild(cell.element);
    }
  }
}
